import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import API from '../../redux/api/b2c-api';
import {PinIcon} from './data'
const MIN_QUERY_LENGTH = 3;
const DEBOUNCE_DELAY_MS = 350;
const BLUR_CLOSE_DELAY_MS = 200;

function debounce(fn, delay) {
  let timerId;
  return function () {
    const args = arguments;
    const context = this;
    clearTimeout(timerId);
    timerId = setTimeout(function () {
      fn.apply(context, args);
    }, delay);
  };
}

function cx() {
  const classNames = Array.prototype.slice.call(arguments);
  return classNames.filter(Boolean).join(' ');
}

function normalizeAddressResponse(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.data)) return data.data;
  if (data && Array.isArray(data.predictions)) return data.predictions;
  return [];
}

async function defaultFetchAddress(query) {
  try {
    const data = await API.autoCompleteAddress(query);
    return normalizeAddressResponse(data);
  } catch (error) {
    return [];
  }
}



function SuggestionItem(props) {
  const suggestion = props.suggestion;
  const isActive = props.isActive;
  const onSelect = props.onSelect;
  const onHover = props.onHover;

  return (
    <button
      type="button"
      className={cx('cap-autocomplete__item', isActive && 'cap-autocomplete__item--active')}
      onMouseDown={onSelect}
      onMouseEnter={onHover}
    >
      <span className="cap-autocomplete__pin">
        <PinIcon size={14} />
      </span>
      <span className="cap-autocomplete__name">{suggestion.description}</span>
    </button>
  );
}

function SuggestionsList(props) {
  const suggestions = props.suggestions;
  const activeIndex = props.activeIndex;
  const onSelect = props.onSelect;
  const onHoverIndex = props.onHoverIndex;

  return (
    <div className="cap-autocomplete">
      {suggestions.map(function (suggestion, index) {
        return (
          <SuggestionItem
            key={suggestion.place_id || index}
            suggestion={suggestion}
            isActive={index === activeIndex}
            onSelect={function () { onSelect(suggestion); }}
            onHover={function () { onHoverIndex(index); }}
          />
        );
      })}
    </div>
  );
}

function useClickOutside(ref, onOutsideClick) {
  useEffect(function () {
    function handleClick(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        onOutsideClick();
      }
    }
    document.addEventListener('mousedown', handleClick);
    return function () {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [ref, onOutsideClick]);
}

function AddressAutocomplete(props) {
  const value = props.value || '';
  const onChange = props.onChange;
  const onBlur = props.onBlur;
  const placeholder = props.placeholder || 'Start typing your address…';
  const className = props.className || '';
  const hasError = props.hasError;
  const fetchAddress = props.fetchAddress || defaultFetchAddress;

  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const wrapRef = useRef(null);

  const searchAddresses = useCallback(
    function (query) {
      if (!query || query.length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setIsOpen(false);
        setIsLoading(false);
        return Promise.resolve();
      }

      setIsLoading(true);
      return fetchAddress(query)
        .then(function (results) {
          setSuggestions(results || []);
          setIsOpen((results || []).length > 0);
          setActiveIndex(0);
        })
        .catch(function () {
          setSuggestions([]);
          setIsOpen(false);
        })
        .finally(function () {
          setIsLoading(false);
        });
    },
    [fetchAddress]
  );

  const debouncedSearch = useMemo(
    function () { return debounce(searchAddresses, DEBOUNCE_DELAY_MS); },
    [searchAddresses]
  );

  useClickOutside(wrapRef, function () { setIsOpen(false); });

  const handleInputChange = useCallback(
    function (e) {
      const nextValue = e.target.value;
      if (onChange) onChange(nextValue);
      debouncedSearch(nextValue);
    },
    [onChange, debouncedSearch]
  );

  const handleSelect = useCallback(
    function (suggestion) {
      if (onChange) onChange(suggestion.description);
      setSuggestions([]);
      setIsOpen(false);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    function (e) {
      if (!isOpen || suggestions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex(function (prev) { return Math.min(prev + 1, suggestions.length - 1); });
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex(function (prev) { return Math.max(prev - 1, 0); });
          break;
        case 'Enter':
          e.preventDefault();
          if (suggestions[activeIndex]) handleSelect(suggestions[activeIndex]);
          break;
        case 'Escape':
          setIsOpen(false);
          break;
        default:
          break;
      }
    },
    [isOpen, suggestions, activeIndex, handleSelect]
  );

  const handleBlur = useCallback(
    function () {
      setTimeout(function () {
        if (onBlur) onBlur();
      }, BLUR_CLOSE_DELAY_MS);
    },
    [onBlur]
  );

  const showSuggestions = isOpen && suggestions.length > 0;
  const showLoadingIndicator = isLoading && value.length >= MIN_QUERY_LENGTH;

  return (
    <div className="cap-address-autocomplete" ref={wrapRef} style={{ position: 'relative' }}>
      <input
        className={cx('cap-input cap-field-box-sm', hasError && 'cap-field-box-sm--error', className)}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        autoComplete="off"
      />

      {showSuggestions && (
        <SuggestionsList
          suggestions={suggestions}
          activeIndex={activeIndex}
          onSelect={handleSelect}
          onHoverIndex={setActiveIndex}
        />
      )}

      {showLoadingIndicator && <div className="cap-address-loading">Searching…</div>}

      <span className="cap-maps-note">
        <PinIcon size={13} />
        Powered by Google Maps
      </span>
    </div>
  );
}

export default AddressAutocomplete;